/**
 * Tenant theme support, mirroring appNext's ThemeService: tenants can override
 * brand colors through the TenantTheme Global Configuration ({ TenantLogo,
 * Attributes: [{ VariableName, Value }] }, served by the admin service). The
 * presets in globals.css are the FBO defaults; when a tenant configuration
 * exists, its attributes are applied on top — both verbatim as
 * `--fbo-theme-{name}` (host parity) and translated onto the shadcn variables
 * the components actually consume.
 */

export interface TenantThemeAttribute {
  variableName: string
  value: string
}

const THEME_PREFIX = "fbo-theme"

interface Rgb {
  r: number
  g: number
  b: number
}

/** "#rgb" / "#rrggbb" → {r,g,b} in 0–255; null if not a hex color */
function parseHex(hex: string): Rgb | null {
  const match = /^#(?:([0-9a-f]{3})|([0-9a-f]{6}))$/i.exec(hex.trim())
  if (!match) return null

  const expanded = match[1] ? [...match[1]].map((c) => c + c).join("") : match[2]
  return {
    r: parseInt(expanded.slice(0, 2), 16),
    g: parseInt(expanded.slice(2, 4), 16),
    b: parseInt(expanded.slice(4, 6), 16),
  }
}

/** {r,g,b} → "h s% l%" (the shadcn CSS-variable format) */
function rgbToHslTriplet({ r, g, b }: Rgb): string {
  const rn = r / 255
  const gn = g / 255
  const bn = b / 255

  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const delta = max - min
  const lightness = (max + min) / 2

  let hue = 0
  if (delta !== 0) {
    if (max === rn) hue = ((gn - bn) / delta) % 6
    else if (max === gn) hue = (bn - rn) / delta + 2
    else hue = (rn - gn) / delta + 4
    hue *= 60
    if (hue < 0) hue += 360
  }

  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1))

  return `${Math.round(hue)} ${Math.round(saturation * 100)}% ${Math.round(lightness * 100)}%`
}

/** "#rgb" / "#rrggbb" → "h s% l%" (the shadcn variable format); null if not hex */
export function hexToHslTriplet(hex: string): string | null {
  const rgb = parseHex(hex)
  return rgb ? rgbToHslTriplet(rgb) : null
}

/** Black or white foreground triplet, whichever contrasts with the given color */
function contrastingForeground({ r, g, b }: Rgb): string {
  // Perceived luminance (ITU-R BT.601)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? "240 3% 12%" : "0 0% 100%"
}

/**
 * Applies tenant theme attributes over the presets. Non-color values (the
 * host's SVG icon attributes) are only exposed as --fbo-theme-* variables.
 *
 * The tenant's `accent-color` drives this microapp's accents (badges, tile
 * edges) — matching how the host paints its active nav highlight and steppers.
 * The primary interactive color (buttons, links, focus rings) is themed ONLY
 * when a tenant explicitly overrides it via `primary-button-background`: the
 * accent is deliberately NOT mapped onto primary, so a standard brand accent
 * (e.g. the host's yellow) leaves primary buttons on the FBO blue the rest of
 * the app uses, rather than turning them yellow.
 */
export function applyTenantTheme(attributes: TenantThemeAttribute[]): void {
  const root = document.documentElement
  const byName = new Map<string, string>()

  for (const attribute of attributes) {
    if (!attribute.variableName || !attribute.value) continue
    byName.set(attribute.variableName, attribute.value)
    // Host parity: every attribute is available under the host's variable names
    root.style.setProperty(`--${THEME_PREFIX}-${attribute.variableName}`, attribute.value)
  }

  // Parses the named attribute's hex once; warns (rather than silently skipping)
  // when a configured attribute isn't a valid hex color, so misconfiguration is
  // diagnosable. Returns null when the attribute is absent or invalid.
  const colorOf = (name: string): Rgb | null => {
    const hex = byName.get(name)
    if (!hex) return null
    const rgb = parseHex(hex)
    if (!rgb) {
      console.warn(`Tenant theme attribute "${name}" is not a hex color ("${hex}"); ignoring.`)
    }
    return rgb
  }

  // Brand accent → accents (badges, tile edges)
  const accent = colorOf("accent-color")
  if (accent) {
    root.style.setProperty("--fbo-accent", rgbToHslTriplet(accent))
    root.style.setProperty("--fbo-accent-foreground", contrastingForeground(accent))
  }

  // Primary → only an explicit button override (never the accent), with --ring
  // tracking it and the foreground from primary-button-color or a contrast pick
  const primary = colorOf("primary-button-background")
  if (primary) {
    const triplet = rgbToHslTriplet(primary)
    root.style.setProperty("--primary", triplet)
    root.style.setProperty("--ring", triplet)
    const foreground = colorOf("primary-button-color")
    root.style.setProperty(
      "--primary-foreground",
      foreground ? rgbToHslTriplet(foreground) : contrastingForeground(primary)
    )
  }

  const sidebar = colorOf("side-nav-text-hover")
  if (sidebar) {
    root.style.setProperty("--sidebar-background", rgbToHslTriplet(sidebar))
  }
}

/** Normalizes the admin service's PascalCase attribute payload */
export function parseThemeAttributes(data: any): TenantThemeAttribute[] {
  const attributes = data?.Attributes ?? data?.attributes
  if (!Array.isArray(attributes)) return []

  return attributes
    .map((attribute: any) => ({
      variableName: String(attribute?.VariableName ?? attribute?.variableName ?? ""),
      value: String(attribute?.Value ?? attribute?.value ?? ""),
    }))
    .filter((attribute) => attribute.variableName && attribute.value)
}
